import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Plus, Trash2, Users, Loader2, Clock, RefreshCw } from 'lucide-react';
import { apiService, type GroupMemberDTO, type RequestOptions } from '@/services/api.service';
import { useAuthStore } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';
import { onGameGroup, type GameGroupLabel } from '@/utils/gameGroups';
import { formatPlayerName } from '@/utils/playerName';
import { isProtectedGrade } from './gradesByGameGroup';
import styles from './GradesPage.module.scss';

interface GradeMembersProps {
  /** Grade LuckPerms (nom exact). */
  name: string;
  /** Groupe du cadre, nommé dans les messages ; null = jeu sans groupes. */
  group: GameGroupLabel | null;
  /** Requêtes à groupe imposé du cadre. */
  options: RequestOptions | undefined;
  protectedGrades: string[];
}

/** « jusqu'au 12/11/2026 » pour un grade temporaire (fin en secondes epoch). */
function expiryLabel(expiry?: number): string | null {
  if (!expiry) return null;
  return `jusqu'au ${new Date(expiry * 1000).toLocaleDateString('fr-FR')}`;
}

/**
 * Onglet Joueurs d'un grade : les joueurs qui le portent directement (LuckPerms du groupe du cadre,
 * sans base de joueurs : sert aussi le lobby), et, pour qui peut le gérer, l'ajout d'un joueur par
 * pseudo Recube, gamertag ou uuid, et le retrait. Mêmes routes et mêmes contrôles que la fiche joueur
 * (POST et DELETE /api/ranks/player : droit d'écriture de la page Joueurs, recube.allow-rank.<grade>
 * dans le LuckPerms du groupe sauf admin, grade protégé réservé aux admins).
 */
export function GradeMembers({ name, group, options, protectedGrades }: GradeMembersProps) {
  const isAdmin = useAuthStore((s) => !!s.user?.isAdmin);
  const canWrite = useAuthStore((s) => s.canWrite);
  // canWrite lit le projet actif à l'appel : abonnement pour redessiner au changement.
  useGameStore((s) => s.activeGameId);
  const canWritePlayers = canWrite('players');

  const [members, setMembers] = useState<GroupMemberDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Grade gérable par le membre du staff dans ce groupe (grades gérables du satellite), null = inconnu.
  const [manageable, setManageable] = useState<boolean | null>(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const loadSeq = useRef(0);

  const isDefault = name.toLowerCase() === 'default';
  const isProtected = isProtectedGrade(name, protectedGrades) && !isAdmin;

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    const res = await apiService.getGroupMembers(name, options);
    if (seq !== loadSeq.current) return;
    if (res.data) {
      setMembers(res.data.members);
      setTotal(res.data.total);
      setTruncated(res.data.truncated);
      setLoadError(null);
    } else {
      setMembers([]);
      setLoadError(res.status === 404
        ? 'liste indisponible : le satellite de ce groupe doit passer en BE-Panel-Games 1.0.15 ou plus'
        : (res.error ?? 'lecture impossible'));
    }
    setLoading(false);
  }, [name, options]);

  useEffect(() => {
    if (isDefault) {
      setLoading(false);
      return;
    }
    void load();
  }, [load, isDefault]);

  // Grades gérables (même lecture que la fiche joueur) : seulement pour qui peut écrire sur les joueurs.
  useEffect(() => {
    if (!canWritePlayers || isDefault || isProtected) {
      setManageable(false);
      return;
    }
    let live = true;
    setManageable(null);
    void apiService.getManageableGroups(options).then((res) => {
      if (!live) return;
      setManageable(!!res.data?.groups.some((g) => g.name.toLowerCase() === name.toLowerCase()));
    });
    return () => { live = false; };
  }, [canWritePlayers, isDefault, isProtected, name, options]);

  const flash = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q || busy) return;
    setBusy('add');
    setError(null);
    const found = await apiService.lookupRankPlayer(q, options);
    if (!found.data) {
      setBusy(null);
      setError(found.status === 404 ? `Joueur introuvable${onGameGroup(group)} : ${q}.` : (found.error ?? 'Recherche impossible.'));
      return;
    }
    const label = formatPlayerName(found.data.name, found.data.displayName, found.data.uuid);
    const res = await apiService.addGroupToPlayer(found.data.uuid, name, options);
    setBusy(null);
    if (res.error) {
      setError(`${label} : ${res.error}`);
      return;
    }
    setQuery('');
    flash(`Grade « ${name} » donné à ${label}${onGameGroup(group)}.`);
    await load();
  };

  const handleRemove = async (member: GroupMemberDTO) => {
    const label = formatPlayerName(member.name, member.displayName, member.uuid);
    if (!window.confirm(`Retirer le grade « ${name} » de ${label}${onGameGroup(group)} ?`)) return;
    setBusy(member.uuid);
    setError(null);
    const res = await apiService.removeGroupFromPlayer(member.uuid, name, options);
    setBusy(null);
    if (res.error) {
      setError(`${label} : ${res.error}`);
      return;
    }
    flash(`Grade « ${name} » retiré à ${label}${onGameGroup(group)}.`);
    await load();
  };

  if (isDefault) {
    return <p className={styles.muted}>Tous les joueurs ont le grade « default » : pas de liste.</p>;
  }

  return (
    <>
      {manageable && (
        <form className={styles.addForm} onSubmit={handleAdd}>
          <input
            className={styles.input}
            placeholder="Pseudo Recube, gamertag ou UUID du joueur"
            aria-label={`Donner le grade ${name} à un joueur`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="submit" className={styles.primaryBtn} disabled={busy !== null || !query.trim()}>
            {busy === 'add' ? <Loader2 size={16} className={styles.spin} /> : <Plus size={16} />} Donner le grade
          </button>
        </form>
      )}
      {error && <span className={styles.error}>{error}</span>}
      {success && <span className={styles.muted}>{success}</span>}

      <div className={styles.sectionLabel}>
        <Users size={14} /> Joueurs ({loading ? '…' : total})
        {!loading && (
          <button type="button" className={styles.memberRefresh} onClick={() => void load()} title="Relire la liste" aria-label="Relire la liste">
            <RefreshCw size={12} />
          </button>
        )}
      </div>
      {loading ? (
        <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement...</span></div>
      ) : loadError ? (
        <span className={styles.error}>Joueurs du grade{onGameGroup(group)} : {loadError}.</span>
      ) : members.length === 0 ? (
        <p className={styles.muted}>Aucun joueur n'a ce grade directement (les grades hérités n'y figurent pas).</p>
      ) : (
        <>
          <ul className={styles.permList}>
            {members.map((m) => {
              const until = expiryLabel(m.expiry);
              return (
                <li key={m.uuid} className={styles.permRow}>
                  <span className={styles.memberName} title={m.uuid}>
                    {formatPlayerName(m.name, m.displayName, m.uuid)}
                    {m.online && (
                      <span className={styles.memberOnline}>en ligne{m.serverName ? ` · ${m.serverName}` : ''}</span>
                    )}
                    {until && <span className={styles.memberExpiry}><Clock size={11} /> {until}</span>}
                  </span>
                  {manageable && (
                    <button
                      className={styles.removeBtn}
                      onClick={() => void handleRemove(m)}
                      disabled={busy !== null}
                      title={`Retirer le grade ${name}`}
                    >
                      {busy === m.uuid ? <Loader2 size={14} className={styles.spin} /> : <Trash2 size={14} />}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          {truncated && (
            <p className={styles.muted}>{members.length} premiers joueurs sur {total} (en ligne d'abord).</p>
          )}
        </>
      )}
      {!canWritePlayers ? (
        <p className={styles.muted}>Lecture seule : donner ou retirer un grade demande l'écriture sur la page Joueurs.</p>
      ) : isProtected ? (
        <p className={styles.muted}>Grade protégé : seuls les admins le donnent ou le retirent.</p>
      ) : manageable === false ? (
        <p className={styles.muted}>Tu ne peux pas gérer ce grade{onGameGroup(group)} (droit recube.allow-rank.{name.toLowerCase()} dans son LuckPerms).</p>
      ) : null}
    </>
  );
}
